import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { Field, Form, Formik, getIn, useFormikContext } from "formik";
import { sortBy } from "lodash";
import * as Yup from "yup";

import { SearchMultipleSelectField } from "../../../../components/src/form";
import type { ISelectOption } from "../../../../components/src/form";
import { Button } from "../../../../components/src/Button";

import { T_ACCOUNT_MAPPING_INSTRUMENTS, UPSERT_T_ACCOUNT_CHART } from "../../../../graphql/src/taccountchart2/queries";

import { AccountingTransactionType, CicTypeEnum, CurrencyEnum, InstrumentModelTypeEnum } from "../../types.generated";
import type { UpsertTAccountChart, UpsertTAccountChartInput, UpsertTAccountMappingInput } from "../../types.generated";

const SELECTOR_FIELDS = ["instruments", "currencies", "modelTypes", "cicTypes"] as const;

// form values use "" for an unselected value and [] for an empty selector criteria
interface MappingValueValues {
    accountingTransactionType: string;
    debit: string;
    credit: string;
}

interface MappingValues {
    name: string;
    selector: Record<(typeof SELECTOR_FIELDS)[number], string[]>;
    values: MappingValueValues[];
}

interface TAccountMappingsFormValues {
    tAccountMappings: MappingValues[];
}

const toOptions = (values: string[]): ISelectOption[] => values.map((value) => ({ key: value, value, text: value }));

const validationSchema = Yup.object({
    tAccountMappings: Yup.array().of(
        Yup.object({
            values: Yup.array()
                .of(
                    Yup.object({
                        accountingTransactionType: Yup.string().required("Type is required"),
                        debit: Yup.string().required("Debit is required"),
                        credit: Yup.string().required("Credit is required")
                    })
                )
                .test("unique-types", "Repeated type", function (values) {
                    const types = (values ?? []).map((value) => value.accountingTransactionType);
                    const repeatedIndex = types.findIndex((type, index) => type && types.indexOf(type) !== index);
                    if (repeatedIndex === -1) return true;
                    return this.createError({
                        path: `${this.path}.${repeatedIndex}.accountingTransactionType`,
                        message: "Repeated type " + types[repeatedIndex]
                    });
                })
        })
    )
});

const FieldError = ({ name }: { name: string }): React.ReactElement => {
    const { errors, touched, submitCount } = useFormikContext<TAccountMappingsFormValues>();
    const error = getIn(errors, name);
    if (!error || typeof error !== "string" || (!getIn(touched, name) && submitCount === 0)) return null;
    return <div className="form-field-error">{error}</div>;
};

interface MappingCardProps {
    index: number;
    instrumentOptions: ISelectOption[];
    tAccountOptions: { number: string; description: string }[];
}

const MappingCard = ({ index, instrumentOptions, tAccountOptions }: MappingCardProps): React.ReactElement => {
    const { values, setFieldValue, isSubmitting } = useFormikContext<TAccountMappingsFormValues>();
    const tAccountMappings = values.tAccountMappings;
    const tAccountMapping = tAccountMappings[index];
    const name = `tAccountMappings.${index}`;
    const displayName = tAccountMapping.name || "mapping " + (index + 1);

    const move = (to: number) => {
        const moved = [...tAccountMappings];
        moved.splice(to, 0, moved.splice(index, 1)[0]);
        setFieldValue("tAccountMappings", moved);
    };

    const selectorOptions: Record<(typeof SELECTOR_FIELDS)[number], { label: string; options: ISelectOption[] }> = {
        instruments: { label: "Instruments", options: instrumentOptions },
        currencies: { label: "Currencies", options: toOptions(Object.values(CurrencyEnum)) },
        modelTypes: { label: "Model types", options: toOptions(Object.values(InstrumentModelTypeEnum)) },
        cicTypes: { label: "CIC types", options: toOptions(Object.values(CicTypeEnum)) }
    };

    return (
        <div className="border rounded p-3 mb-3" data-testid={`mapping-${index}`}>
            <div className="flex items-end gap-2 mb-2">
                <div className="font-semibold w-8">{index + 1}</div>
                <div className="flex-1">
                    <label htmlFor={`${name}.name`} className="font-semibold">
                        Name
                    </label>
                    <Field id={`${name}.name`} name={`${name}.name`} className="form-input" />
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Move ${displayName} up`}
                    disabled={isSubmitting || index === 0}
                    onClick={() => move(index - 1)}
                >
                    Up
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Move ${displayName} down`}
                    disabled={isSubmitting || index === tAccountMappings.length - 1}
                    onClick={() => move(index + 1)}
                >
                    Down
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Remove ${displayName}`}
                    disabled={isSubmitting}
                    onClick={() =>
                        setFieldValue(
                            "tAccountMappings",
                            tAccountMappings.filter((_, i) => i !== index)
                        )
                    }
                >
                    Remove
                </Button>
            </div>
            <div className="grid [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))] gap-x-2">
                {SELECTOR_FIELDS.map((selectorField) => (
                    <SearchMultipleSelectField
                        key={selectorField}
                        name={`${name}.selector.${selectorField}`}
                        label={
                            <label htmlFor={`${name}.selector.${selectorField}`} className="font-semibold">
                                {selectorOptions[selectorField].label}
                            </label>
                        }
                        options={selectorOptions[selectorField].options}
                        placeholder="Any"
                        clearable
                    />
                ))}
            </div>
            {tAccountMapping.values.length > 0 ? (
                <div className="flex gap-2 mb-1 font-semibold">
                    <div className="flex-1">Transaction type</div>
                    <div className="flex-1">Debit</div>
                    <div className="flex-1">Credit</div>
                    {/* above the remove buttons */}
                    <div className="w-20 shrink-0"></div>
                </div>
            ) : null}
            {tAccountMapping.values.map((_, valueIndex) => {
                const valueName = `${name}.values.${valueIndex}`;
                return (
                    <div key={valueIndex} className="flex items-start gap-2 mb-1">
                        <div className="flex-1">
                            <Field
                                as="select"
                                name={`${valueName}.accountingTransactionType`}
                                aria-label={`${valueName}.accountingTransactionType`}
                                className="form-input"
                            >
                                <option value=""></option>
                                {Object.values(AccountingTransactionType).map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </Field>
                            <FieldError name={`${valueName}.accountingTransactionType`} />
                        </div>
                        {(["debit", "credit"] as const).map((side) => (
                            <div key={side} className="flex-1">
                                <Field as="select" name={`${valueName}.${side}`} aria-label={`${valueName}.${side}`} className="form-input">
                                    <option value=""></option>
                                    {/* keep a stored account that is not in the chart visible so it is not lost silently */}
                                    {tAccountMapping.values[valueIndex][side] &&
                                    !tAccountOptions.some((tAccount) => tAccount.number === tAccountMapping.values[valueIndex][side]) ? (
                                        <option value={tAccountMapping.values[valueIndex][side]}>
                                            {tAccountMapping.values[valueIndex][side]} (not in chart)
                                        </option>
                                    ) : null}
                                    {tAccountOptions.map((tAccount) => (
                                        <option key={tAccount.number} value={tAccount.number}>
                                            {tAccount.number} {tAccount.description}
                                        </option>
                                    ))}
                                </Field>
                                <FieldError name={`${valueName}.${side}`} />
                            </div>
                        ))}
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="w-20 shrink-0"
                            aria-label={`Remove ${valueName}`}
                            disabled={isSubmitting}
                            onClick={() =>
                                setFieldValue(
                                    `${name}.values`,
                                    tAccountMapping.values.filter((__, i) => i !== valueIndex)
                                )
                            }
                        >
                            Remove
                        </Button>
                    </div>
                );
            })}
            <Button
                type="button"
                size="sm"
                variant="secondary"
                aria-label={`Add debit / credit to ${displayName}`}
                disabled={isSubmitting}
                onClick={() =>
                    setFieldValue(`${name}.values`, [...tAccountMapping.values, { accountingTransactionType: "", debit: "", credit: "" }])
                }
            >
                Add debit / credit
            </Button>
        </div>
    );
};

interface TAccountMappingsFormProps {
    tAccountChart: UpsertTAccountChart;
    onDone: (saved: boolean) => void;
}

export function TAccountMappingsForm({ tAccountChart, onDone }: TAccountMappingsFormProps): React.ReactElement {
    const [{ data: instrumentsData }] = useQuery<{ instruments: { _id: string; name: string }[] }>({
        query: T_ACCOUNT_MAPPING_INSTRUMENTS
    });
    const [_, upsertTAccountChart] = useMutation(UPSERT_T_ACCOUNT_CHART);
    const [saveError, setSaveError] = useState<string | null>(null);

    const instrumentOptions = useMemo(
        () =>
            sortBy(instrumentsData?.instruments ?? [], "name").map((instrument) => ({
                key: instrument._id,
                value: instrument._id,
                text: instrument.name
            })),
        [instrumentsData]
    );

    // debit and credit can only be accounts in the chart
    const tAccountOptions = useMemo(
        () =>
            sortBy(tAccountChart.tAccounts ?? [], "number").map((tAccount) => ({
                number: tAccount.number,
                description: tAccount.description ?? ""
            })),
        [tAccountChart]
    );

    const initialValues: TAccountMappingsFormValues = useMemo(
        () => ({
            tAccountMappings: (tAccountChart.tAccountMappings ?? []).map((tAccountMapping) => ({
                name: tAccountMapping.name ?? "",
                selector: {
                    instruments: tAccountMapping.selector.instruments ?? [],
                    currencies: tAccountMapping.selector.currencies ?? [],
                    modelTypes: tAccountMapping.selector.modelTypes ?? [],
                    cicTypes: tAccountMapping.selector.cicTypes ?? []
                },
                values: (tAccountMapping.values ?? []).map((value) => ({
                    accountingTransactionType: value.accountingTransactionType,
                    debit: value.debit,
                    credit: value.credit
                }))
            }))
        }),
        [tAccountChart]
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                setSaveError(null);
                const tAccountMappings: UpsertTAccountMappingInput[] = values.tAccountMappings.map((tAccountMapping) => ({
                    name: tAccountMapping.name || null,
                    // an empty criteria matches everything, stored as null
                    selector: {
                        instruments: tAccountMapping.selector.instruments.length > 0 ? tAccountMapping.selector.instruments : null,
                        currencies:
                            tAccountMapping.selector.currencies.length > 0 ? (tAccountMapping.selector.currencies as CurrencyEnum[]) : null,
                        modelTypes:
                            tAccountMapping.selector.modelTypes.length > 0
                                ? (tAccountMapping.selector.modelTypes as InstrumentModelTypeEnum[])
                                : null,
                        cicTypes: tAccountMapping.selector.cicTypes.length > 0 ? (tAccountMapping.selector.cicTypes as CicTypeEnum[]) : null
                    },
                    values: tAccountMapping.values.map((value) => ({
                        accountingTransactionType: value.accountingTransactionType as AccountingTransactionType,
                        debit: value.debit,
                        credit: value.credit
                    }))
                }));
                // an update replaces the whole chart, send the accounts unchanged without the __typename urql adds
                const input: UpsertTAccountChartInput = {
                    _id: tAccountChart._id,
                    clientId: tAccountChart.clientId,
                    name: tAccountChart.name,
                    version: tAccountChart.version,
                    tAccounts: JSON.parse(
                        JSON.stringify(tAccountChart.tAccounts ?? [], (key, v) => (key === "__typename" ? undefined : v))
                    ),
                    tAccountMappings,
                    updateTimestamp: tAccountChart.updateTimestamp
                };
                const result = await upsertTAccountChart({ input });
                if (result.error) {
                    setSaveError(result.error.graphQLErrors.length > 0 ? result.error.graphQLErrors[0].message : result.error.message);
                } else {
                    onDone(true);
                }
            }}
        >
            {({ values, setFieldValue, isSubmitting, dirty }) => (
                <Form autoComplete="off">
                    <div className="flex gap-2 mb-2">
                        <Button type="submit" size="sm" disabled={isSubmitting || !dirty}>
                            Save
                        </Button>
                        <Button type="button" size="sm" variant="secondary" disabled={isSubmitting} onClick={() => onDone(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={isSubmitting}
                            onClick={() =>
                                setFieldValue("tAccountMappings", [
                                    ...values.tAccountMappings,
                                    { name: "", selector: { instruments: [], currencies: [], modelTypes: [], cicTypes: [] }, values: [] }
                                ])
                            }
                        >
                            Add mapping
                        </Button>
                    </div>
                    {saveError ? <div className="form-field-error mb-2">{saveError}</div> : null}
                    {values.tAccountMappings.map((_, index) => (
                        <MappingCard key={index} index={index} instrumentOptions={instrumentOptions} tAccountOptions={tAccountOptions} />
                    ))}
                </Form>
            )}
        </Formik>
    );
}
